// src/pages/FileImagePage.tsx
// Standalone image viewer for /Dashboard/FileImage/#/[imageId]
// Reads the image ID from the URL hash, validates permissions, and shows
// the image with zoom/pan — no MainLayout, no navigation arrows.

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import { apiService } from '../services/api';
import { downloadFile } from '../services/imagesGalleryService';
import { getMaxZoomScale } from '../lib/imageZoom';
import './FileImagePage.css';

// Baseline zoom ceiling; getMaxZoomScale raises it for images fitted below natural size
const DEFAULT_MAX_ZOOM_SCALE = 8;

interface FilePermissionsResponse {
  HasPermissions: boolean;
  Id: number;
  IsReply: boolean;
}

interface FileImageItem {
  AttachmentId: number;
  EmbeddedImageId: number;
  FileName: string;
  FileUrl: string;
  FileExtension: string;
  Description: string;
  Title?: string;
}

// ---------------------------------------------------------------------------
// Center Image button — must live inside TransformWrapper to use useControls
// ---------------------------------------------------------------------------
const CenterImageButton: React.FC = () =>
{
  const { centerView } = useControls();
  return (
    <button
      type="button"
      className="fi-center-btn"
      title="Center Image"
      onClick={() => centerView(1)}
    >
      <i className="material-icons">crop_free</i>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export const FileImagePage: React.FC = () =>
{
  const location = useLocation();

  // Hash: "#/{id}" for conversation images, "#/Chat/{fileName}" for chat images
  const rawHash = location.hash.replace(/^#\/?/, '').trim();
  const isFromChat = /^chat\//i.test(rawHash);
  const imageId = isFromChat ? rawHash.replace(/^chat\//i, '').trim() : rawHash;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageItem, setImageItem] = useState<FileImageItem | null>(null);
  const [maxZoomScale, setMaxZoomScale] = useState(DEFAULT_MAX_ZOOM_SCALE);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  useEffect(() =>
  {
    if (!imageId)
    {
      setError('No image ID provided.');
      setLoading(false);
      return;
    }

    const loadImage = async () =>
    {
      setLoading(true);
      setError(null);
      try
      {
        // Validate permissions (chat images require the isFromChat flag)
        const perms = await apiService.get<FilePermissionsResponse>(
          `File/GetUrlFileImageByPermissions?fileName=${imageId}${isFromChat ? '&isFromChat=true' : ''}`,
        );

        if (!perms.HasPermissions)
        {
          setError('You do not have permission to view this image.');
          return;
        }

        // Chat and conversation images use different endpoints
        if (isFromChat)
        {
          // Chat/GetFileImages returns a single object, not an array
          const chatImage = await apiService.get<FileImageItem>(
            `Chat/GetFileImages?fileName=${imageId}`,
          );

          if (!chatImage || !chatImage.FileUrl)
          {
            setError('Image not found.');
            return;
          }

          setImageItem(chatImage);
          return;
        }

        const images = await apiService.get<FileImageItem[]>(
          `Conversation/GetFileImages?id=${imageId}&IsReply=${perms.IsReply}`,
        );

        if (!images || images.length === 0)
        {
          setError('Image not found.');
          return;
        }

        setImageItem(images[0] ?? null);
      }
      catch (err)
      {
        console.error('FileImagePage: failed to load image', err);
        setError('Failed to load image. Please make sure you are logged in.');
      }
      finally
      {
        setLoading(false);
      }
    };

    loadImage();
  }, [imageId, isFromChat]);

  // ---- Download ----
  const handleDownload = useCallback(async () =>
  {
    if (!imageItem) return;
    const fileName =
      (imageItem.Description || imageItem.FileName || 'image') + (imageItem.FileExtension || '');
    try
    {
      await downloadFile(
        imageItem.AttachmentId,
        imageItem.EmbeddedImageId,
        fileName,
        imageItem.FileUrl,
      );
    }
    catch (err)
    {
      console.error('FileImagePage: download failed', err);
    }
  }, [imageItem]);

  // ---- Right-click context menu ----
  const handleContextMenu = useCallback((e: React.MouseEvent) =>
  {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // Close context menu on any click outside
  useEffect(() =>
  {
    document.addEventListener('click', closeContextMenu);
    return () => document.removeEventListener('click', closeContextMenu);
  }, [closeContextMenu]);

  // ---- Render states ----
  if (loading)
  {
    return (
      <div className="fi-page">
        <div className="fi-spinner" />
      </div>
    );
  }

  if (error || !imageItem)
  {
    return (
      <div className="fi-page fi-page--error">
        <i className="material-icons fi-error-icon">broken_image</i>
        <p className="fi-error-text">{error || 'Image not available.'}</p>
      </div>
    );
  }

  return (
    <div className="fi-page" onContextMenu={handleContextMenu}>
      <TransformWrapper
        initialScale={1}
        minScale={0.1}
        maxScale={maxZoomScale}
        centerOnInit
        centerZoomedOut={false}
        limitToBounds={false}
        panning={{ velocityDisabled: true }}
      >
        {/* Center Image button — inside TransformWrapper to access useControls */}
        <CenterImageButton />

        <TransformComponent
          wrapperStyle={{ width: '100vw', height: '100vh' }}
          contentStyle={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <img
            src={imageItem.FileUrl}
            alt={imageItem.Description || 'Image'}
            className="fi-image"
            draggable={false}
            onLoad={e => setMaxZoomScale(getMaxZoomScale(e.currentTarget, DEFAULT_MAX_ZOOM_SCALE))}
            onContextMenu={e => e.preventDefault()}
          />
        </TransformComponent>
      </TransformWrapper>

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          className="fi-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={e => e.stopPropagation()}
        >
          <div
            className="fi-context-menu-item"
            onClick={() =>
            {
              handleDownload();
              setContextMenu(null);
            }}
          >
            <i className="material-icons fi-context-icon">file_download</i>
            Download Image
          </div>
        </div>
      )}
    </div>
  );
};

export default FileImagePage;
